import { Vector2 } from '@/utils/vec';
import type { XlsxFile } from '@/utils/xlsx';
import { RichText } from '@/utils/xlsx/richText';
import type { Cell, SheetPart } from '@/utils/xlsx/sheetPart';
import { useEffect, useMemo, useRef, useState } from 'react';

export function XlsxViewer({ xlsx }: { xlsx: XlsxFile }) {
  const {
    workbook: { sheets },
  } = xlsx;

  const [selected, setSelected] = useState(0);

  return (
    <div className="flex h-full w-full flex-col bg-white p-8">
      <div className="flex items-center justify-center">
        <select
          value={selected}
          onChange={e => setSelected(parseInt(e.target.value))}
        >
          {sheets.map((sheet, index) => (
            <option key={sheet.name} value={index}>
              {sheet.name}
            </option>
          ))}
        </select>
      </div>
      <div className="flex w-full flex-1 overflow-hidden">
        <SheetViewer sheet={sheets[selected]!} />
      </div>
    </div>
  );
}

function SheetViewer({ sheet }: { sheet: SheetPart }) {
  const divRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(new Vector2(0, 0));
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  useEffect(() => {
    if (!divRef.current) return;
    setSize(
      new Vector2(divRef.current.offsetWidth, divRef.current.offsetHeight),
    );
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setScrollLeft(e.currentTarget.scrollLeft);
    setScrollTop(e.currentTarget.scrollTop);
  };

  const { cells } = useMemo(
    () =>
      sheet.getVisibleCells(
        new Vector2(scrollLeft, scrollTop),
        new Vector2(scrollLeft + size.x, scrollTop + size.y),
      ),
    [scrollLeft, scrollTop, sheet, size.x, size.y],
  );

  return (
    <div
      ref={divRef}
      className="relative h-full w-full overflow-auto"
      onScroll={handleScroll}
    >
      {cells.map(cell => (
        <div
          key={cell.ref}
          className="absolute border border-gray-400"
          style={{
            left: cell.pos.col * 8.43 * 10,
            top: cell.pos.row * 20,
            width: 8.43 * 10,
            height: 20,
          }}
        >
          <CellViewer cell={cell} />
        </div>
      ))}
      <div
        className="absolute bg-red-400/50"
        style={{
          left: scrollLeft + size.x - 50,
          top: scrollTop + size.y - 50,
          width: 100,
          height: 100,
        }}
      />
    </div>
  );
}

function CellViewer({ cell }: { cell: Cell }) {
  if (cell.value instanceof RichText) {
    return (
      <div
        dangerouslySetInnerHTML={{ __html: cell.value.toHtml().outerHTML }}
      />
    );
  }

  return <div>{cell.value}</div>;
}
